import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CTA, CustomInput, CustomSelect, Text } from '../../../atoms';
import ButtonsBanner from '../ButtonsBanner';
import {
  getValues,
  isValidForm,
  newForm,
  newFormField,
} from '../../../../utils/form';
import { RegexpType, validator } from '../../../../utils/validator';
import {
  COURSE_TAB_SAVE,
  SURVEY_LIST_GET,
  TUTOR_DISABLED_TAB_SET,
} from '../../../../app/redux/actions/tutors';
import InputLegend from '../../../atoms/InputLegend';
import { CustomBox, Separator } from '../style';
import {
  CATALOGS_GET,
  COURSE_CATEGORIES_GET,
  LEVELS_GET,
} from '../../../../app/redux/actions/courselist';
import CustomToggle from '../../../atoms/CustomToggle';
import { MODAL_OPEN } from '../../../../app/redux/actions';
import CustomTextarea from '../../../atoms/CustomTextarea';
import ModalTags, { ModalTagsId } from '../../../atoms/Modals/ModalTags';
import TagBadge from '../../../atoms/TagBadge';
import { InputContainer, TagsWrapper } from './style';
import ImagesUploader from '../../ImagesUploader';
import { hasPermission } from '../../../../utils/permissions';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import theme from '../../../theme';
import ModalRelatedCourses, {
  ModalRelatedCoursesId,
} from '../../../atoms/Modals/ModalRelatedCourses';
import { RelatedCourseCards } from '../../../atoms/Modals/ModalRelatedCourses/style';
import { ModalCategoryMediaId } from '../../../atoms/Modals/ModalCategoryMedia';
import { REGION_PROVINCE_LIST_GET } from '../../../../app/redux/actions/user';
import {
  formatProvinceOpt,
  formatRegionOpt,
  formatSelectOpt,
} from '../../../../utils/labels';

const initialForm = newForm([
  newFormField({
    field: 'catalog_id',
    required: true,
    type: RegexpType.NUMBER,
    valid: true,
  }),
  newFormField({
    field: 'region',
    required: false,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'province',
    required: false,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'title',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'category',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'subject',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'published',
    required: false,
    value: false,
    type: RegexpType.BOOLEAN,
    valid: true,
  }),
  newFormField({
    field: 'participation_percentage',
    required: true,
    type: RegexpType.PERCENTAGE,
    valid: true,
  }),
  newFormField({
    field: 'course_cover',
    required: false,
    value: false,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'tags',
    value: [],
    required: false,
    valid: true,
  }),
  newFormField({
    field: 'survey',
    required: false,
    valid: true,
  }),
  newFormField({
    field: 'description',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'panoramic_media',
    required: false,
    value: false,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'related_courses',
    value: [],
    required: false,
    valid: true,
  }),
]);

const EventOverviewTab = ({
  mediaIsPhone,
  setDisabledTab,
  catalogs,
  getCatalogs,
  categoriesAll,
  getCategories,
  levels,
  getLevels,
  activeTab,
  courseTabSave,
  permissions,
  modalOpen,
  course,
  isSupervisor,
  isAdmin,
  regionProvinceGet,
  regionProvinceList,
  getSurveyList,
  surveys,
}) => {
  const { editMode, overview } = course;
  const [form, setForm] = useState({ ...initialForm });
  const [images, setImages] = useState({});
  const [cover, setCover] = useState();
  const [media, setMedia] = useState();
  const [resetFormDone, setResetFormDone] = useState(false);
  const [code, setCode] = useState();
  const [categoryId, setCategoryId] = useState();

  // tutor title
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [tutorTitle, setTutorTitle] = useState({
    province: '',
    office: '',
    title: '',
  });

  useEffect(() => {
    if (editMode) {
      const newEditableForm = { ...initialForm };
      newEditableForm.catalog_id.value = overview?.catalog_id;
      newEditableForm.category.value = formatSelectOpt(overview?.category);
      newEditableForm.description.value = overview?.description;
      newEditableForm.published.value = overview?.published;
      newEditableForm.tags.value = overview?.tags;
      newEditableForm.title.value = overview?.title;
      newEditableForm.course_cover.value = overview?.course_cover?.id;
      newEditableForm.related_courses.value = overview?.related_courses;
      newEditableForm.region.value = overview?.region;
      newEditableForm.province.value = overview?.province;
      newEditableForm.participation_percentage.value =
        overview?.participation_percentage || 100;
      newEditableForm.participation_percentage.required = false;
      newEditableForm.survey.value = overview.satisfaction_survey;
      if (overview?.subject) {
        newEditableForm.subject.value = formatSelectOpt(overview?.subject);
      } else {
        newEditableForm.subject.required = false;
      }

      setCode(overview?.code);
      // media
      setCover(overview?.course_cover);
      setMedia(overview?.panoramic_media);
      // update forms
      setForm(newEditableForm);
      // split tutor title
      splitTutorTitle(overview?.title);
    } else {
      const resetForm = { ...initialForm };
      Object.keys(resetForm).forEach((k) => {
        resetForm[k].value = null;
      });
      setForm(resetForm);
      courseTabSave({ tabName: 'overview', data: {} });
      if (catalogs?.length <= 0) getCatalogs();
      setResetFormDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  useEffect(() => {
    getCategories({ show_all: true });
    regionProvinceGet();
    if (levels?.length <= 0) getLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //? if you edit the select it comes as "id", if preconfigured it comes as "value"
    const category_id = form.category?.value?.value || form.category?.value?.id;
    if (category_id) {
      handleOnChange('survey', null);
      getSurveyList(category_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category?.value]);

  useEffect(() => {
    // need to set catalog_id if is in create flow
    if (resetFormDone && !editMode && catalogs?.length > 0) {
      const newEditableForm = { ...form };
      const catalog = catalogs.find((c) => c?.code === 'CL');
      newEditableForm.catalog_id = {
        ...form.catalog_id,
        value: Number(catalog?.id),
      };
      setForm(newEditableForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, resetFormDone, catalogs]);

  useEffect(() => {
    if (form?.category?.value) {
      if (!form.category?.value?.children?.length > 0) {
        setForm({
          ...form,
          subject: {
            ...form.subject,
            value: null,
            valid: true,
            required: false,
          },
        });
      } else {
        setForm({
          ...form,
          subject: {
            ...form.subject,
            value: null,
            valid: true,
            required: true,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category?.value]);

  const handleOnChange = useCallback(
    (field = '', newValue) => {
      const { type, required } = form[field];
      setForm({
        ...form,
        [field]: {
          ...form[field],
          value: newValue,
          valid:
            typeof newValue !== 'object'
              ? validator(type, newValue, required)
              : true,
        },
      });
    },
    [form]
  );

  useEffect(() => {
    if (isValidForm(form) && cover) {
      setDisabledTab({
        1: { completed: true },
        2: { disabled: false },
      });
    } else {
      setDisabledTab({
        1: { completed: false },
        2: { disabled: true },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidForm(form), cover]);

  const getCatalogCodeById = (id) => {
    return catalogs.find((c) => c.id === id)?.code;
  };

  useEffect(() => {
    // console.log('+++ FORM +++', form);
    return () => {
      if (activeTab === 1 && isValidForm(form)) {
        courseTabSave({ tabName: 'overview', data: getValues(form) });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, form]);

  // Tutor title
  useEffect(() => {
    setRegions(regionProvinceList.map((r) => formatRegionOpt(r)));
  }, [regionProvinceList]);

  const splitTutorTitle = (fullTitle = '') => {
    const titleArray = fullTitle.split('-');
    if (titleArray.length >= 3) {
      setTutorTitle({
        province: titleArray[0]?.trim(),
        office: titleArray[1]?.trim(),
        title: titleArray[2]?.trim(),
      });
    }
  };

  useEffect(() => {
    if (getCatalogCodeById(form?.catalog_id?.value) === 'CL') {
      if (tutorTitle.province && tutorTitle.office && tutorTitle.title) {
        handleOnChange(
          'title',
          `${tutorTitle.province} - ${tutorTitle.office} - ${tutorTitle.title}`
        );
      } else handleOnChange('title', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorTitle]);

  return (
    <div>
      <Text type="h1.5" mb={8}>
        Anagrafica
      </Text>
      <Text type="b3" mb={44}>
        Sezione dedicata ai dati e impostazioni principali del percorso
        formativo che si sta creando.
      </Text>
      <InputLegend
        label="Tipologia evento"
        htmlFor="course-catalog"
        required={form.catalog_id.required}
        tooltipKey={!(isSupervisor || isAdmin) ? 'course_catalog' : ''}
      />
      <CustomSelect
        disabled={!(isSupervisor || isAdmin) || editMode}
        id="course-catalog"
        options={catalogs.map((c) => ({
          label: c?.name || '',
          value: c.id,
        }))}
        size={mediaIsPhone ? 'full' : 's'}
        onChange={(newValue) => handleOnChange('catalog_id', newValue?.value)}
        selected={
          catalogs
            .map((c) => ({
              label: c?.name || '',
              value: c.id,
            }))
            .find((opt) => opt.value === form.catalog_id?.value) || {
            label: 'Catalogo Locale',
          }
        }
      />
      <Separator />
      <InputContainer>
        <div>
          <InputLegend
            label="Seleziona l’area tematica"
            required
            htmlFor="course-category"
          />
          <CustomSelect
            size={mediaIsPhone ? 'full' : 'm'}
            id="course-category"
            options={categoriesAll}
            selected={form.category?.value}
            onChange={(newValue) => {
              setCode();
              const oldValue = form.category?.value;
              if (newValue !== oldValue) {
                const updatedForm = { ...form };
                updatedForm.category.value = newValue;
                updatedForm.survey.value = null;
                setForm(updatedForm);
              }
              setCategoryId(newValue?.id);
            }}
            disabled={editMode && !isSupervisor}
          />
        </div>
        {(form.subject?.value ||
          form.category?.value?.children?.length > 0) && (
          <div>
            <InputLegend
              label="Seleziona la materia"
              required
              htmlFor="course-subject"
            />
            <CustomSelect
              size={mediaIsPhone ? 'full' : 'm'}
              id="course-subject"
              options={
                categoriesAll?.find(
                  (c) =>
                    c.id ===
                    (form?.category?.value?.id || form?.category?.value?.value)
                )?.children
              }
              selected={form.subject?.value}
              onChange={(newValue) => {
                setCode();
                handleOnChange('subject', newValue);
              }}
              disabled={editMode && !isSupervisor}
            />
          </div>
        )}
        <div>
          <InputLegend
            label="Codice percorso formativo"
            tooltipKey="course_code"
            htmlFor="course-code"
          />
          <CustomSelect
            disabled
            size={mediaIsPhone ? 'full' : 's'}
            id="course-code"
            selected={{
              label:
                code ||
                `${getCatalogCodeById(form?.catalog_id?.value) || 'CX'}-${
                  form.category?.value?.code || 'XX'
                }-XXX`,
            }}
          />
        </div>
      </InputContainer>
      <Separator />
      <div className="d-flex g-48">
        <InputLegend
          label="Rendi visibile il percorso formativo"
          mb={0}
          tooltipKey="course_published"
          htmlFor="course-published"
        />
        <CustomToggle
          checked={form.published.value || false}
          id="course-published"
          onClick={() => handleOnChange('published', !form.published.value)}
        />
      </div>
      <Separator />
      {catalogs.find((c) => c.id === form.catalog_id.value)?.code === 'CN' ? (
        // NATIONAL TITLE
        <InputContainer>
          <div>
            <InputLegend
              label="Titolo percorso formativo"
              required={form.title.required}
              tooltipKey="course_title"
              htmlFor="course-title"
            />
            <CustomInput
              placeholder="Inserisci"
              id="course-title"
              onChange={(newValue) => handleOnChange('title', newValue)}
              value={form.title.value || ''}
              size={mediaIsPhone ? 'full' : 'm'}
            />
          </div>
          <div>
            <InputLegend
              label='Aggiungi prefisso "NAZ - " al titolo'
              mb={16}
              htmlFor="course-naz"
            />
            <CustomToggle
              checked={!!form.title.value?.includes('NAZ - ')}
              id="course-naz"
              onClick={() => {
                let newTitle = form.title.value;
                if (newTitle?.includes('NAZ - ')) {
                  newTitle = newTitle.replace('NAZ - ', '');
                } else {
                  newTitle = `NAZ - ${newTitle || ''}`;
                }
                handleOnChange('title', newTitle);
              }}
            />
          </div>
        </InputContainer>
      ) : (
        // LOCAL TITLE
        <>
          <Text type="h3" mb={8}>
            Titolo percorso formativo
          </Text>
          <Text type="b3" mb={24}>
            Per definire il titolo inserisci le seguenti informazioni:
          </Text>
          <InputContainer>
            {!editMode && (
              <>
                <div>
                  <InputLegend
                    label="Regione"
                    required={!isSupervisor}
                    tooltipKey="course_title_region"
                    htmlFor="course-title-region"
                  />
                  <CustomSelect
                    options={regions}
                    id="course-title-region"
                    onChange={(v) => {
                      const updatedForm = { ...form };
                      updatedForm.region.value = v?.value;
                      updatedForm.province.value = undefined;
                      if (!isSupervisor) {
                        // reset title only if is not supervisor
                        updatedForm.title.value = undefined;
                      }
                      setForm(updatedForm);
                      setProvinces(
                        (v?.provinces || []).map((p) => formatProvinceOpt(p))
                      );
                    }}
                    selected={regions.find(
                      (opt) => opt.value === form.region.value
                    )}
                    isSearchable
                    size={mediaIsPhone ? 'full' : 's'}
                  />
                </div>
                <div>
                  <InputLegend
                    label="Provincia"
                    required={!isSupervisor}
                    tooltipKey="event_title_province"
                    htmlFor="course-title-province"
                  />
                  <CustomSelect
                    options={provinces}
                    id="course-title-province"
                    onChange={(v) => {
                      handleOnChange('province', v?.value);
                      if (!isSupervisor) {
                        setTutorTitle({
                          ...tutorTitle,
                          province: v.code || '',
                        });
                      }
                    }}
                    selected={
                      provinces.find(
                        (opt) => opt.value === form.province.value
                      ) || null
                    }
                    isSearchable
                    size={mediaIsPhone ? 'full' : 's'}
                  />
                </div>
              </>
            )}
            {isSupervisor ? (
              <div>
                <InputLegend
                  label="Titolo percorso formativo"
                  required={form.title.required}
                  tooltipKey="course_title_tutor"
                  htmlFor="course-title-tutor"
                />
                <CustomInput
                  placeholder="Inserisci"
                  id="course-title-tutor"
                  onChange={(newValue) => handleOnChange('title', newValue)}
                  value={form.title.value || ''}
                  size={mediaIsPhone ? 'full' : 'm'}
                />
              </div>
            ) : (
              <>
                <div>
                  <InputLegend
                    label="Titolo ufficio"
                    required={form.title.required}
                    tooltipKey="event_title_office"
                    htmlFor="event-title-office"
                  />
                  <CustomInput
                    placeholder="Inserisci"
                    id="event-title-office"
                    onChange={(newValue) =>
                      setTutorTitle({ ...tutorTitle, office: newValue })
                    }
                    value={tutorTitle.office || ''}
                    size={mediaIsPhone ? 'full' : 's'}
                  />
                </div>
                <div>
                  <InputLegend
                    label="Titolo evento"
                    required={form.title.required}
                    tooltipKey="event_title_tutor"
                    htmlFor="event-title-tutor"
                  />
                  <CustomInput
                    placeholder="Inserisci"
                    id="event-title-tutor"
                    onChange={(newValue) =>
                      setTutorTitle({ ...tutorTitle, title: newValue })
                    }
                    value={tutorTitle.title || ''}
                    size={mediaIsPhone ? 'full' : 'm'}
                  />
                </div>
              </>
            )}
          </InputContainer>
        </>
      )}
      <Separator />
      <InputContainer>
        <div>
          <InputLegend
            label="Immagine evento"
            required
            tooltipKey="event_cover"
          />
          {cover ? (
            <div className="d-flex g-24">
              <Text type="b3">{cover?.title || cover?.name}</Text>
              <CTA
                type="tertiary"
                btnText="Elimina"
                icon="trash-01"
                onClick={() => {
                  handleOnChange('course_cover', undefined);
                  setCover();
                }}
                disabled={editMode && !hasPermission(permissions, 'set_images')}
              />
            </div>
          ) : (
            <div
              className={
                mediaIsPhone ? 'd-flex flex-col align-start' : 'd-flex g-24'
              }
            >
              <CTA
                disabled={editMode && !hasPermission(permissions, 'set_images')}
                btnText="Seleziona file"
                onClick={() =>
                  modalOpen(ModalCategoryMediaId, {
                    onConfirm: (img) => {
                      setCover(img);
                      if (img?.id) {
                        handleOnChange('course_cover', img?.id);
                      } else {
                        setImages({ ...images, course_cover: img });
                      }
                    },
                  })
                }
                fullsize={mediaIsPhone}
              />
              <Text type="b3">Nessun file selezionato</Text>
            </div>
          )}
        </div>
        <div>
          <InputLegend
            label="Percentuale di partecipazione"
            required={form.participation_percentage.required}
            tooltipKey="participation_percentage"
            htmlFor="participation-percentage"
          />
          <div style={{ position: 'relative' }}>
            <CustomInput
              placeholder="Inserisci"
              id="participation-percentage"
              type="number"
              onChange={(newValue) =>
                handleOnChange('participation_percentage', newValue)
              }
              value={form.participation_percentage.value || ''}
              size={mediaIsPhone ? 'full' : 's'}
              valid={form.participation_percentage.valid}
              error="Il valore deve essere compreso tra 1 e 100"
              errorPosition="absolute"
              disabled={editMode}
            />
            <Text type="b3" className="percentage-label">
              %
            </Text>
          </div>
        </div>
      </InputContainer>
      <Separator />
      <Text type="h3" mb={12}>
        TAG
      </Text>
      <TagsWrapper>
        {form.tags.value?.length > 0 && (
          <>
            {(form.tags.value || []).map((t, i) => (
              <TagBadge
                key={`added-tag-${i}`}
                item={t}
                isActive
                onClick={() => {
                  const tempTags = [...form.tags.value];
                  tempTags.splice(i, 1);
                  handleOnChange('tags', tempTags);
                }}
              />
            ))}
          </>
        )}
        <CTA
          btnText="Aggiungi TAG"
          onClick={() => modalOpen(ModalTagsId)}
          fullsize={mediaIsPhone}
        />
        {!form.tags.value?.length && (
          <Text type="b3" color="#646464">
            Nessun TAG aggiunto
          </Text>
        )}
      </TagsWrapper>
      <Separator />
      <InputLegend label="Questionario di gradimento" htmlFor="course-survey" />
      <CustomSelect
        key={categoryId} //? trick to reset select after category change
        size={mediaIsPhone ? 'full' : 'm'}
        id="course-survey"
        options={surveys.map((c) => ({
          label: c.title,
          value: c.id,
        }))}
        selected={surveys
          .map((c) => ({
            label: c.title,
            value: c.id,
          }))
          .find(
            (opt) => String(opt.value) === String(form.survey?.value?.node_id)
          )}
        onChange={(newValue) => {
          handleOnChange('survey', newValue);
        }}
        isClearable
      />
      <Separator />
      <CustomBox>
        <Text type="h1.5" mb={8}>
          Panoramica
        </Text>
        <Text type="b3" mb={40}>
          Sezione dedicata al contenuto redazionale del percorso formativo che
          si sta creando. Viene mostrato al discente nel dettaglio corso, sotto
          la voce del tab “Panoramica”
        </Text>
        <InputLegend
          label="Descrizione"
          required={form.description.required}
          htmlFor="event-description"
        />
        <CustomTextarea
          placeholder="Inserisci qui il testo"
          value={form.description?.value || ''}
          onChange={(value) => handleOnChange('description', value)}
          id="event-description"
        />
        <div>
          <InputLegend
            label="Contenuto multimediale"
            tooltipKey="course_panoramic_media"
            type="secondary"
          />
          {media?.title || media?.name ? (
            <div className="d-flex g-24">
              <Text type="b3">{media?.title || media?.name}</Text>
              <CTA
                type="tertiary"
                btnText="Elimina"
                icon="trash-01"
                onClick={() => {
                  handleOnChange('panoramic_media', undefined);
                  setMedia();
                }}
                disabled={editMode && !hasPermission(permissions, 'set_images')}
              />
            </div>
          ) : (
            <div
              className={
                mediaIsPhone ? 'd-flex flex-col align-start' : 'd-flex g-24'
              }
            >
              <CTA
                disabled={editMode && !hasPermission(permissions, 'set_images')}
                btnText="Seleziona file"
                onClick={() =>
                  modalOpen(ModalCategoryMediaId, {
                    includeVideos: true,
                    onConfirm: (img) => {
                      setMedia(img);
                      if (img?.id) {
                        handleOnChange('panoramic_media', img?.id);
                      } else {
                        setImages({ ...images, panoramic_media: img });
                      }
                    },
                  })
                }
                fullsize={mediaIsPhone}
              />
              <Text type="b3">Nessun file selezionato</Text>
            </div>
          )}
        </div>
        <Separator />
        <InputLegend label="Percorsi formativi correlati" />
        {form?.related_courses?.value?.length > 0 && (
          <>
            <Text type="b3" mb={24}>
              Hai aggiunto {form?.related_courses?.value?.length} percorsi
              formativi correlati:
            </Text>
            <RelatedCourseCards>
              {(form?.related_courses?.value || []).map((rc) => (
                <div className="related-card" key={`relate-course-${rc?.id}`}>
                  <Text type="b4" className="code">
                    {rc?.code}
                  </Text>
                  <Text type="h3" className="ellipsis">
                    {rc?.title}
                  </Text>
                  <CTA
                    bottomRight
                    type="tertiary"
                    btnText="Rimuovi"
                    icon="x"
                    onClick={() => {
                      const newCourses = [...form.related_courses.value];
                      const courseId = newCourses?.findIndex(
                        (c) => c?.id === rc?.id
                      );
                      newCourses.splice(courseId, 1);
                      handleOnChange('related_courses', newCourses);
                    }}
                  />
                </div>
              ))}
            </RelatedCourseCards>
          </>
        )}
        <div className={mediaIsPhone ? '' : 'd-flex g-24'}>
          <CTA
            fullsize={mediaIsPhone}
            btnText="Aggiungi percorso formativo"
            onClick={() => modalOpen(ModalRelatedCoursesId)}
          />
          {!form.related_courses.value?.length > 0 && (
            <Text type="b3" color="#646464" mt={mediaIsPhone ? 16 : 0}>
              Nessun percorso formativo aggiunto
            </Text>
          )}
        </div>
      </CustomBox>
      {mediaIsPhone && (
        <Text type="b3" className="mt-5" color={theme().colors.fail}>
          * campo obbligatorio
        </Text>
      )}
      <ButtonsBanner hasMandatoryFields />
      <ModalTags
        addedTags={form.tags.value}
        setAddedTags={(newTags) => handleOnChange('tags', newTags)}
      />
      <ModalRelatedCourses
        addedCourses={[...(form.related_courses.value || [])]}
        setAddedCourses={(newCourses) =>
          handleOnChange('related_courses', newCourses)
        }
      />
      <ImagesUploader images={images} />
    </div>
  );
};

EventOverviewTab.propTypes = {
  mediaIsPhone: PropTypes.bool,
  // HOC: state
  categoriesAll: PropTypes.array,
  catalogs: PropTypes.array,
  levels: PropTypes.array,
  course: PropTypes.object,
  permissions: PropTypes.array,
  isSupervisor: PropTypes.bool,
  isAdmin: PropTypes.bool,
  regionProvinceList: PropTypes.array,
  surveys: PropTypes.array,

  // HOC: dispatch
  regionProvinceGet: PropTypes.func,
  getCategories: PropTypes.func,
  getCatalogs: PropTypes.func,
  getLevels: PropTypes.func,
  getSurveyList: PropTypes.func,
  setDisabledTab: PropTypes.func,
  courseTabSave: PropTypes.func,
  activeTab: PropTypes.number,
  modalOpen: PropTypes.func,
};

export default connect(
  (state) => {
    const { categoriesAll, catalogs, levels } = state.courselist;
    const { activeTab, course, surveys } = state.tutors;
    const { course: permissions } = state.permissions;
    const { isSupervisor, isAdmin, regionProvinceList } = state.user;
    return {
      categoriesAll,
      catalogs,
      levels,
      activeTab,
      course,
      permissions,
      isSupervisor,
      isAdmin,
      regionProvinceList,
      surveys,
    };
  },
  (dispatch) => ({
    regionProvinceGet: () =>
      dispatch({ type: REGION_PROVINCE_LIST_GET._REQUEST }),
    setDisabledTab: (tabs) => dispatch({ type: TUTOR_DISABLED_TAB_SET, tabs }),
    courseTabSave: ({ tabName, data }) =>
      dispatch({ type: COURSE_TAB_SAVE, tabName, data }),
    getCategories: (filters) =>
      dispatch({ type: COURSE_CATEGORIES_GET._REQUEST, filters }),
    getCatalogs: () => dispatch({ type: CATALOGS_GET._REQUEST }),
    getLevels: () => dispatch({ type: LEVELS_GET._REQUEST }),
    getSurveyList: (id) =>
      dispatch({ type: SURVEY_LIST_GET._REQUEST, id, typology: 'event' }),
    modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
  })
)(withMediaQueries(EventOverviewTab));
